<template>
  <div>
    <div class="page-header page-header-small">
      <parallax
        class="page-header-image"
        style="background-image: url('/img/office.jpg')"
      >
      </parallax>
      <div class="content-center">
        <div class="container">
          <h1 class="title">For Developers.</h1>
        </div>
      </div>
    </div>



    <div class="section section-about-us">
      <div class="container">
        <div class="row">
          <div class="col-md-8 ml-auto mr-auto">
            <h2 class="title">Events API</h2>
            <div>
              Get Crowdify event information identified by multiple parameters.
            </div>
            <div class="divspace metadata">
              <code>Base URL: https://api.crowdify.io/v1</code>
            </div>

            <div class="divspace metadata">
              <div><b>Version: V1.0</b></div>
              <div>
                <b>Status: </b>
                <v-chip
                  class="ma-2"
                  color="#68EDC6"
                  text-color="white"
                  small
                >
                  live
                </v-chip>
              </div>
            </div>

            <div class="divspace">
              <v-chip
                class="ma-2"
                label
                color="#68EDC6"
              >
                GET
              </v-chip>
              <span class="metadata">
                /events
              </span>
            </div>

            <div style="margin-left:30px">

              <div style="padding-top:40px">
                <h3>Authentication</h3>
                The way we ensure Crowdify data is secured for developers and users alike is through authentication.
                As an authentication method we use OAuth 2.0 Bearer Token.
                This authentication method requires you to pass a Bearer Token with your request, which you can request with your client_id and client_secret through the POST oauth2/token endpoint.
                Just get in touch with us at <a href="mailto:team@crowdify.io?subject=Customer Onboarding">team@crowdify.io</a> so that we can create an account for you.
              </div>

              <div style="padding-top:40px">
                <h3>Request</h3>

                <b-table striped fixed hover :items="header_items"></b-table>
                <br />
                <b-table striped fixed hover :items="parameter_items"></b-table>


                <h4>Example</h4>
                <v-card
                  color="#385F73"
                >
                  <div style="padding:20px">
                    <v-card-text class="headline" style="color:#ffffff">
                      curl -X GET "https://api.crowdify.io/v1/events/?lat=48.7791878&lng=9.1071759&radius=19002&start=2020-08-30T08:13:00.000Z&end=2020-10-20T08:12:00.000Z&limit=20&state=active" -H "authorizationToken:YOUR_BEARER_TOKEN>"
                    </v-card-text>
                  </div>
                </v-card>
                <div class="divspace">
                  You must replace <code>YOUR_BEARER_TOKEN</code> with the Bearer Token that you requested with your app client_id and client_secret.
                </div>
              </div>

              <div style="padding-top:40px">
                <h3>Response</h3>

                On success, the HTTP status code in the response header is 200 OK and the response body contains an array of event objects in JSON format.

                If no events are found, the array under the key "data" is empty. On error, the header status code is an error code and the response body contains an error message.

                <b-table striped hover :items="response_items" class="divspace"></b-table>



                <h4>Pagination</h4>
                A response contains 20 Event objects by default. In order to get additional Events, you need to handle pagination. To do so, you will need to access the <code>next_token</code> from the response to get the additional Event objects back for the next 20 Events until there is no longer a next_token available.
                With the <code>previous_token</code> you can query the previous 20 Events.

                <h4>Example</h4>
                <v-card
                  color="#385F73"
                >
                  <div style="padding:20px" >

                    <v-card-text style="color:#ffffff" >
                      <textarea name="" id="myTextareaEvents" cols="30" rows="43" style="resize: none;" v-model="textedJsonEventsResponse"></textarea>
                    </v-card-text>
                  </div>
                </v-card>
              </div>

            </div> <!-- end:margin -->

            <h2 class="title" style="padding-top:80px">Authentication API</h2>

            <div class="divspace metadata">
              <code>Base URL: https://auth.crowdify.io</code>
            </div>

            <div class="divspace metadata">
              <div><b>Version: V1.0</b></div>
              <div>
                <b>Status: </b>
                <v-chip
                  class="ma-2"
                  color="#68EDC6"
                  text-color="white"
                  small
                >
                  live
                </v-chip>
              </div>
            </div>

            <div class="divspace">
              <v-chip
                class="ma-2"
                label
                color="#68EDC6"
              >
                POST
              </v-chip>
              <span class="metadata">
                /oauth2/token
              </span>
            </div>

            <div style="margin-left:30px">

              <div style="padding-top:40px">
                <h3>Request</h3>
                The access token request requires a basic authorization header constructed by base64-encoding the client_id and client_secret together.

                <b-table striped hover :items="auth_header_items"></b-table>

                <div class="divspace">
                  The Authorization field is constructed as follows:
                  <ul style="margin-left: 0em; padding-left: 3em; padding-top:0.5em">
                    <li>
                      The <code>client_id</code> and <code>client_secret</code> are combined with a single colon (:)
                    </li>
                    <li>
                      The resulting string is encoded using a variant of Base64
                    </li>
                    <li>
                    The authorization method and a space (e.g. "Basic ") is then prepended to the encoded string
                    </li>
                  </ul>
                  For example:<br />
                  Take your <code>client_id:foo</code> and your <code>client_secrect:bar</code> and transform them to:
                  <ul style="margin-left: 0em; padding-left: 3em; padding-top:0.5em">
                    <li>
                      Combine with a single colon --> foo:bar
                    </li>
                    <li>
                      Base64 encode it --> Zm9vOmJhcg==
                    </li>
                    <li>
                      Add the prefix "Basic " --> Basic Zm9vOmJhcg==
                    </li>
                  </ul>

                  For testing purposes, you can encode a string with following commands: <br /><br />
                  <b>Mac:</b>
                  <v-card
                    color="#385F73"
                  >
                    <div style="padding:20px">
                      <v-card-text class="headline" style="color:#ffffff">
                        echo -n foo:bar | base64
                      </v-card-text>
                    </div>
                  </v-card>
                </div> <!-- end:divspace -->
                <br />
                <b>Windows:</b>
                <v-card
                  color="#385F73"
                >
                  <div style="padding:20px">
                    <v-card-text class="headline" style="color:#ffffff">
                      base64.bat -encode "foo:bar"
                    </v-card-text>
                  </div>
                </v-card>
              </div> <!-- end:paddingtop -->


              <h4>Example</h4>

              <v-card
                color="#385F73"
              >
                <div style="padding:20px">
                  <v-card-text class="headline" style="color:#ffffff">
                  curl -X POST 'https://auth.crowdify.io/oauth2/token' \ <br />
                  --header 'Authorization: Basic NXVrYXEyNW5sYTluYWZmc...zJyMjQ2ODl1Z2tmaTVz' \<br />
                  --header 'Content-Type: application/x-www-form-urlencoded' \<br />
                  --data-urlencode 'grant_type=client_credentials'                    </v-card-text>
                </div>
              </v-card>


              <div style="padding-top:40px">
                <h3>Response</h3>
                <v-card
                  color="#385F73"
                >
                  <div style="padding:20px">
                    <v-card-text class="headline" style="color:#ffffff">
                      <textarea class="headline" id="myTextareaAuthentication" name="" cols="15" rows="5" style="resize: none;" v-model="textedJsonAuthenticationResponse"></textarea>
                    </v-card-text>
                  </div>
                </v-card>
              </div><!-- end:paddingTop -->
            </div><!-- end:marginTop -->


            <div class="divspace">
              <span style="font-size:2em; font-weight: 900">Supporting ressources:</span>
              <div>
                <router-link v-popover:popover1 class="nav-link" to="/documentation">
                    <span style="font-size:1.5em">Documentation Overview</span>
                    <i class="now-ui-icons arrows-1_minimal-right"></i>
                </router-link>
              </div>
              <div>
                <router-link v-popover:popover1 class="nav-link" to="/documentation/dashboard/start">
                    <span style="font-size:1.5em">Dashboard</span>
                    <i class="now-ui-icons arrows-1_minimal-right"></i>
                </router-link>
              </div>
            </div>

            <div style="padding-top:40px">
              <div style="font-size:2em; font-weight: 900; color:#C51162">Ready to build your solution?</div>
              <div style="font-size:3em; font-weight: 900;">Apply for access to get started</div>
              <v-btn
                rounded
                color="rgba(255, 255, 255, 0.7)"
                href="mailto:team@crowdify.io?subject=Customer Onboarding"
              >
                Apply for access
              </v-btn>
            </div>



          </div>


          </div> <!-- end:col bootstrap -->
        </div> <!-- end:row -->
      </div> <!-- end:container section -->
    </div> <!-- end:about us -->




</template>
<script>
import DocumentationFooter from './components/DocumentationFooter';
import { Auth } from 'aws-amplify'

import { Button } from '@/components';
export default {
  name: 'Landing',
  bodyClass: 'landing-page',
  components: {
    [Button.name]: Button,
    DocumentationFooter
  },
  data() {
    return {
      header_items: [
            { HEADER: 'authorizationToken', TYPE: 'String', REQUIRED: 'Required' }
      ],
      parameter_items: [
            { QUERY_PARAMETER: 'lat',  TYPE: 'Number', _: 'Required' },
            { QUERY_PARAMETER: 'lng', TYPE: 'Number', _: 'Required' },
            { QUERY_PARAMETER: 'radius', TYPE: 'String', _: 'Required. The search radius around a Point described by lat and lng. Default: 10000 Meter. Minimum: 1 Meter. Maximum: 20000 Meter. ' },
            { QUERY_PARAMETER: 'start', TYPE: 'String', _: 'Required' },
            { QUERY_PARAMETER: 'end', TYPE: 'String', _: 'Required' },
            { QUERY_PARAMETER: 'state', TYPE: 'String', _: 'Optional' },
            { QUERY_PARAMETER: 'limit', TYPE: 'Number', _: 'Optional. The maximum number of objects to return. Default: 50. Minimum: 1. Maximum: 100.' }
      ],
      auth_header_items: [
            { HEADER: 'Authorization', TYPE: 'String', REQUIRED: 'Required', EXAMPLE: 'Basic NXVrYXEyNW5sYTluYWZmc ... zJyMjQ2ODl1Z2tmaTVz' },
            { HEADER: 'Content-Type', TYPE: 'String', REQUIRED: 'Required', EXAMPLE: 'application/x-www-form-urlencoded' }
      ],
      response_items: [
            { CODE: 200, DESCRIPTION: '	OK - The request has succeeded. The client can read the result of the request in the body and the headers of the response.'},
            { CODE: 403, DESCRIPTION: 'Forbidden - The server understood the request, but is refusing it due to missing authorizations'},
            { CODE: 429, DESCRIPTION: 'Quota exceeded - The server understood the request, but is refusing it due to an exceeded quota'}
      ],
      textedJsonEventsResponse: "",
      rawEventsResponse: "",
      rawAuthenticationResponse: "",
      textedJsonAuthenticationResponse: ""
    };
  },
  mounted() {
    this.rawEventsResponse = {
      "next_token": "7745796edab0d9cc9eeee5c817f6cb12!NDguNzc5MTg3OC8jLzkuMTA3MTc1OS8jLzE5MDAyLyMvMjAyMC0wOC0zMFQwODoxMzowMC4wMDBaLyMvMjAyMC0xMC0yMFQwODoxMjowMC4wMDBaLyMvYWN0aXZl!MjAyMC0wOS0yNVQxODowMDowMFo=",
      "data": [
          {
              "title": "Thees Uhlmann - \"Danke für die Angst\" Trio",
              "category": "concert",
              "start_date": "2020-09-01T17:30:00Z",
              "end_date": "2020-09-01T20:00:00Z",
              "location": {
                  "coordinates": [
                      9.172502640006817,
                      48.804247000000004
                  ],
                  "name": "Freilichtbühne Killesberg"
              },
              "state": "active",
              "updated": "2020-08-29T14:00:49Z",
              "id": "41005d741112dcc143e710d6b574cb55"
          },
          {
              "title": "Cranko Lebt! - Thomas Aders im Gespräch mit den Cranko Stars",
              "category": "concert",
              "start_date": "2020-09-02T17:30:00Z",
              "end_date": "2020-09-02T20:00:00Z",
              "location": {
                  "coordinates": [
                      9.21725646568217,
                      48.7969789
                  ],
                  "name": "Cannstatter Wasen"
              },
              "state": "active",
              "updated": "2020-08-29T14:00:53Z",
              "id": "790aaedb226452d902fc6e15279ab08a"
          }
      ]
    };
    this.rawAuthenticationResponse = {
        "access_token": "eyJraWQiOiJ...5wjHdHx7BykHyPA59g",
        "expires_in": 3600,
        "token_type": "Bearer"
    }
    this.textedJsonEventsResponse = JSON.stringify(this.rawEventsResponse, undefined, 4);
    this.textedJsonAuthenticationResponse = JSON.stringify(this.rawAuthenticationResponse, undefined, 4);

  }
};
</script>
<style>

.metadata {
    font-size: 1.2em;
    font-weight: 800;
    margin-bottom: 15px;
    vertical-align: middle;
}

.divspace {
  margin-top: 30px;
}


textarea {
  width: 100%;
  font-family: "Lucida Console", Monaco, monospace;
  font-size: 0.6rem;
  line-height: 1.0;
}

</style>
